import { useState } from 'react';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Button, Dialog, EmptyState, Field, Skeleton, StatusBadge, TableWrap, type Tone } from '../components/ui.tsx';
import { locale, t, tn, type MessageKey } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { dateTime, since } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { ComposeDialog } from './OutreachMail.tsx';
import { Kpi, Table } from './Reports.tsx';

/**
 * What came back from prospects: their replies (with what each one wants), the demos they booked,
 * and the results from the first e-mail down to new customers.
 */

export type Intent = 'interested' | 'demo' | 'question' | 'not_now' | 'not_interested' | 'unsubscribe' | 'wrong_person' | 'out_of_office' | 'other';
export const INTENTS: Intent[] = ['interested', 'demo', 'question', 'not_now', 'not_interested', 'unsubscribe', 'wrong_person', 'out_of_office', 'other'];
const INTENT_LOOK: Record<Intent, { tone: Tone; icon: IconName }> = {
  interested: { tone: 'ok', icon: 'trendUp' },
  demo: { tone: 'ok', icon: 'calendar' },
  question: { tone: 'info', icon: 'help' },
  not_now: { tone: 'warn', icon: 'clock' },
  not_interested: { tone: 'neutral', icon: 'x' },
  unsubscribe: { tone: 'danger', icon: 'bellOff' },
  wrong_person: { tone: 'warn', icon: 'users' },
  out_of_office: { tone: 'neutral', icon: 'moon' },
  other: { tone: 'neutral', icon: 'mail' },
};
type ReplyFilter = 'new' | 'all' | 'good' | 'later' | 'no' | 'auto';
const REPLY_FILTERS: ReplyFilter[] = ['new', 'good', 'later', 'no', 'auto', 'all'];

interface ReplyRow {
  id: string;
  company: string;
  contact: string | null;
  message: string | null;
  source: 'mailbox' | 'person';
  from_email: string | null;
  from_name: string | null;
  subject: string | null;
  body: string;
  received_at: string;
  intent: Intent;
  intent_by: 'rules' | 'ai' | 'person';
  intent_note: string | null;
  automatic: boolean;
  state: 'new' | 'done';
  handled_at: string | null;
  handled_by_name: string | null;
  company_name: string;
  company_stage: string;
  lead: string | null;
  contact_name: string | null;
  answered_subject: string | null;
  answered_at: string | null;
}

export function IntentBadge({ intent, by }: { intent: Intent; by?: ReplyRow['intent_by'] }) {
  const look = INTENT_LOOK[intent];
  return (
    <span className="orp-intent">
      <StatusBadge tone={look.tone} icon={look.icon} label={t(`orp.in_${intent}` as MessageKey)} />
      {by === 'ai' ? (
        <span className="orp-by" title={t('orp.by_ai')}>
          <Icon name="sparkle" size={12} /> {t('orp.ai')}
        </span>
      ) : null}
    </span>
  );
}

// ---- replies ------------------------------------------------------------------------------------

export function RepliesTab({ onOpenCompany }: { onOpenCompany: (id: string) => void }) {
  const [filter, setFilter] = useState<ReplyFilter>('new');
  const [open, setOpen] = useState<string | null>(null);
  const { data, reload } = useAction<{ rows: ReplyRow[]; more: boolean; counts: Record<ReplyFilter, number> }>('outreach.replies', { filter });
  return (
    <section className="out-section" aria-label={t('out.tab_replies')}>
      <p className="muted small orp-how">
        <Icon name="info" size={14} /> {t('orp.how')}
      </p>
      <div className="out-filters">
        <div className="segmented out-seg" role="group" aria-label={t('orp.filter')}>
          {REPLY_FILTERS.map((f) => (
            <button key={f} type="button" aria-pressed={filter === f} className={filter === f ? 'active' : ''} onClick={() => setFilter(f)}>
              {t(`orp.fl_${f}` as MessageKey)}
              {data ? <span className="orp-count">{data.counts[f]}</span> : null}
            </button>
          ))}
        </div>
      </div>
      {!data ? (
        <Skeleton h={240} />
      ) : !data.rows.length ? (
        filter === 'new' && data.counts.all ? (
          <EmptyState icon="checkCircle" title={t('orp.all_answered')} body={t('orp.all_answered_hint')} />
        ) : (
          <EmptyState icon="reply" title={t('orp.empty')} body={t('orp.empty_hint')} />
        )
      ) : (
        <ul className="orp-list">
          {data.rows.map((r) => (
            <li key={r.id}>
              <button type="button" className={`orp-row${r.state === 'new' ? ' orp-new' : ''}`} onClick={() => setOpen(r.id)}>
                <span className="orp-row-head">
                  <span className="vat-party">
                    <span className="strong">{r.contact_name ?? r.from_name ?? r.from_email ?? t('out.someone')}</span>
                    <span className="muted small">{r.company_name}</span>
                  </span>
                  <span className="orp-row-meta">
                    <IntentBadge intent={r.intent} by={r.intent_by} />
                    <span className="muted small">{since(r.received_at)}</span>
                  </span>
                </span>
                <span className="orp-excerpt">{r.intent_note ?? r.body}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {open ? <ReplyDialog id={open} onClose={() => setOpen(null)} onChanged={reload} onOpenCompany={(id) => (setOpen(null), onOpenCompany(id))} /> : null}
    </section>
  );
}

function ReplyDialog({ id, onClose, onChanged, onOpenCompany }: { id: string; onClose: () => void; onChanged: () => Promise<unknown>; onOpenCompany: (id: string) => void }) {
  const { can } = useSession();
  const manage = can('outreach.manage');
  const { data: r, reload } = useAction<ReplyRow>('outreach.reply', { id });
  const { busy, run } = useSave();
  const [writing, setWriting] = useState(false);
  const [booking, setBooking] = useState(false);
  const changed = async () => {
    await reload();
    await onChanged();
  };
  if (!r) {
    return (
      <Dialog title={t('common.loading')} onClose={onClose}>
        <Skeleton h={260} />
      </Dialog>
    );
  }
  const who = r.contact_name ?? r.from_name ?? r.from_email ?? t('out.someone');
  return (
    <Dialog
      title={t('orp.from', { who })}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <span className="foot-actions">
            <Button variant="ghost" icon="store" onClick={() => onOpenCompany(r.company)}>
              {t('orp.open_company')}
            </Button>
          </span>
          {manage ? (
            <Button
              variant={r.state === 'new' ? 'primary' : 'secondary'}
              icon={r.state === 'new' ? 'checkCircle' : 'undo'}
              busy={busy}
              onClick={() =>
                void run(
                  async () => {
                    await action('outreach.reply_done', { id: r.id, done: r.state === 'new' });
                    await changed();
                    if (r.state === 'new') onClose();
                  },
                  r.state === 'new' ? t('orp.done_saved') : t('orp.undone_saved'),
                )
              }
            >
              {r.state === 'new' ? t('orp.mark_done') : t('orp.mark_new')}
            </Button>
          ) : null}
        </>
      }
    >
      <div className="form-stack">
        <div className="orp-meta">
          <span className="vat-party">
            <span className="strong">{r.company_name}</span>
            <span className="muted small">
              {[r.from_email, dateTime(r.received_at), r.source === 'person' ? t('orp.typed_in') : null].filter(Boolean).join(' · ')}
            </span>
          </span>
          {r.state === 'done' ? <StatusBadge tone="neutral" icon="checkCircle" label={r.handled_by_name ? t('orp.handled_by', { who: r.handled_by_name }) : t('orp.handled')} /> : null}
        </div>
        {r.answered_subject ? (
          <p className="muted small">
            <Icon name="reply" size={14} /> {t('orp.answers', { subject: r.answered_subject })}
          </p>
        ) : null}
        <blockquote className="orp-body">
          {r.subject ? <span className="strong">{r.subject}</span> : null}
          <span>{r.body}</span>
        </blockquote>
        <section className="panel" aria-labelledby="orp-what">
          <div className="panel-head">
            <h2 id="orp-what">{t('orp.what')}</h2>
            <span className="muted small">{r.intent_by === 'person' ? t('orp.by_person') : r.intent_by === 'ai' ? t('orp.by_ai') : t('orp.by_rules')}</span>
          </div>
          {r.intent_note ? (
            <p className="orp-note">
              <Icon name="sparkle" size={14} /> {r.intent_note}
            </p>
          ) : null}
          <div className="orp-choices" role="radiogroup" aria-label={t('orp.what')}>
            {INTENTS.map((i) => {
              const look = INTENT_LOOK[i];
              return (
                <button
                  key={i}
                  type="button"
                  role="radio"
                  aria-checked={r.intent === i}
                  className={`chip${r.intent === i ? ' active' : ''}`}
                  disabled={!manage || busy}
                  onClick={() =>
                    void run(async () => {
                      await action('outreach.set_intent', { id: r.id, intent: i });
                      await changed();
                    }, t('orp.intent_saved', { intent: t(`orp.in_${i}` as MessageKey) }))
                  }
                >
                  <Icon name={look.icon} size={14} />
                  {t(`orp.in_${i}` as MessageKey)}
                </button>
              );
            })}
          </div>
          <p className="muted small">{t(`orp.does_${r.intent}` as MessageKey)}</p>
        </section>
        {r.lead ? (
          <p className="callout callout-ok">
            <Icon name="trendUp" size={16} />
            <span>{t('orp.has_lead')}</span>
            <a className="link" href={`/crm?lead=${r.lead}`}>
              {t('orp.open_lead')}
            </a>
          </p>
        ) : null}
        {manage && !r.automatic ? (
          <div className="row-actions">
            {r.contact && can('outreach.send') && r.intent !== 'unsubscribe' ? (
              <Button variant="secondary" icon="send" onClick={() => setWriting(true)}>
                {t('orp.write_back')}
              </Button>
            ) : null}
            <Button variant="secondary" icon="calendar" onClick={() => setBooking(true)}>
              {t('odm.arrange')}
            </Button>
          </div>
        ) : null}
      </div>
      {writing && r.contact ? <ComposeDialog contacts={[r.contact]} onClose={() => setWriting(false)} onSent={changed} /> : null}
      {booking ? <DemoForm company={r.company} contact={r.contact} demo={null} onClose={() => setBooking(false)} onSaved={async () => (setBooking(false), await changed())} /> : null}
    </Dialog>
  );
}

/** A reply that came another way (a phone call, someone's own mailbox), typed in on the company's card. */
export function RecordReplyDialog({
  people,
  onClose,
  onDone,
}: {
  people: { id: string; name: string }[];
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const [contact, setContact] = useState(people[0]?.id ?? '');
  const [text, setText] = useState('');
  const [intent, setIntent] = useState<Intent | ''>('');
  const { busy, run } = useSave();
  return (
    <Dialog
      title={t('orp.record')}
      onClose={onClose}
      footer={
        <Button
          variant="primary"
          icon="check"
          busy={busy}
          disabled={!contact || !text.trim()}
          onClick={() =>
            void run(async () => {
              await action('outreach.record_reply', { contact, text: text.trim(), intent: intent || null });
              await onDone();
              onClose();
            }, t('orp.recorded'))
          }
        >
          {t('orp.record_save')}
        </Button>
      }
    >
      <div className="form-stack">
        <p className="muted">{t('orp.record_hint')}</p>
        <Field label={t('orp.who')} required>
          <select className="input" value={contact} onChange={(e) => setContact(e.target.value)}>
            {people.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('orp.said')} required counter={`${text.length}/10000`}>
          <textarea className="input" rows={5} maxLength={10_000} value={text} onChange={(e) => setText(e.target.value)} />
        </Field>
        <Field label={t('orp.what')} hint={t('orp.what_hint')}>
          <select className="input" value={intent} onChange={(e) => setIntent(e.target.value as Intent | '')}>
            <option value="">{t('orp.read_for_me')}</option>
            {INTENTS.filter((i) => i !== 'out_of_office').map((i) => (
              <option key={i} value={i}>
                {t(`orp.in_${i}` as MessageKey)}
              </option>
            ))}
          </select>
        </Field>
      </div>
    </Dialog>
  );
}

// ---- demos --------------------------------------------------------------------------------------

type DemoState = 'requested' | 'confirmed' | 'done' | 'cancelled' | 'no_show';
const DEMO_LOOK: Record<DemoState, { tone: Tone; icon: IconName }> = {
  requested: { tone: 'warn', icon: 'clock' },
  confirmed: { tone: 'ok', icon: 'checkCircle' },
  done: { tone: 'info', icon: 'check' },
  cancelled: { tone: 'neutral', icon: 'x' },
  no_show: { tone: 'danger', icon: 'alert' },
};
interface DemoRow {
  id: string;
  company: string;
  contact: string | null;
  at: string;
  minutes: number;
  note: string | null;
  phone: string | null;
  state: DemoState;
  source: 'link' | 'person';
  owner: string | null;
  company_name: string;
  contact_name: string | null;
  contact_email: string | null;
  owner_name: string | null;
}
type DemoFilter = 'upcoming' | 'past' | 'all';

const dayHead = (at: string) => new Intl.DateTimeFormat(locale(), { weekday: 'long', day: 'numeric', month: 'long' }).format(new Date(at));
const hourOf = (at: string) => new Intl.DateTimeFormat(locale(), { hour: '2-digit', minute: '2-digit' }).format(new Date(at));

export function DemoBadge({ state }: { state: DemoState }) {
  return <StatusBadge tone={DEMO_LOOK[state].tone} icon={DEMO_LOOK[state].icon} label={t(`odm.st_${state}` as MessageKey)} />;
}

export function DemosTab({ onOpenCompany }: { onOpenCompany: (id: string) => void }) {
  const [filter, setFilter] = useState<DemoFilter>('upcoming');
  const [open, setOpen] = useState<DemoRow | null>(null);
  const { data, reload } = useAction<{ rows: DemoRow[]; counts: { requested: number; confirmed: number } }>('outreach.demos', { filter });
  const groups: { day: string; rows: DemoRow[] }[] = [];
  for (const d of data?.rows ?? []) {
    const key = dayHead(d.at);
    if (groups.at(-1)?.day !== key) groups.push({ day: key, rows: [] });
    groups.at(-1)!.rows.push(d);
  }
  return (
    <section className="out-section" aria-label={t('out.tab_demos')}>
      <div className="out-filters">
        <div className="segmented out-seg" role="group" aria-label={t('odm.filter')}>
          {(['upcoming', 'past', 'all'] as DemoFilter[]).map((f) => (
            <button key={f} type="button" aria-pressed={filter === f} className={filter === f ? 'active' : ''} onClick={() => setFilter(f)}>
              {t(`odm.fl_${f}` as MessageKey)}
            </button>
          ))}
        </div>
        {data && data.counts.requested ? (
          <span className="muted small">
            <Icon name="clock" size={14} /> {tn('odm.to_confirm', data.counts.requested)}
          </span>
        ) : null}
      </div>
      {!data ? (
        <Skeleton h={200} />
      ) : !data.rows.length ? (
        <EmptyState icon="calendar" title={filter === 'upcoming' ? t('odm.empty') : t('odm.empty_past')} body={t('odm.empty_hint')} />
      ) : (
        groups.map((g) => (
          <section key={g.day} className="odm-day" aria-label={g.day}>
            <h3 className="odm-day-head">{g.day}</h3>
            <ul className="odm-list">
              {g.rows.map((d) => (
                <li key={d.id}>
                  <button type="button" className="odm-row" onClick={() => setOpen(d)}>
                    <span className="odm-time">
                      <span className="strong">{hourOf(d.at)}</span>
                      <span className="muted small">{tn('odm.minutes', d.minutes)}</span>
                    </span>
                    <span className="vat-party">
                      <span className="strong">{d.company_name}</span>
                      <span className="muted small">{[d.contact_name, d.owner_name ? t('odm.with', { who: d.owner_name }) : null].filter(Boolean).join(' · ')}</span>
                    </span>
                    <span className="odm-state">
                      <DemoBadge state={d.state} />
                      {d.source === 'link' ? (
                        <span className="muted small">
                          <Icon name="link" size={12} /> {t('odm.from_link')}
                        </span>
                      ) : null}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
      {open ? <DemoDialog demo={open} onClose={() => setOpen(null)} onChanged={reload} onOpenCompany={(id) => (setOpen(null), onOpenCompany(id))} /> : null}
    </section>
  );
}

function DemoDialog({ demo: d, onClose, onChanged, onOpenCompany }: { demo: DemoRow; onClose: () => void; onChanged: () => Promise<unknown>; onOpenCompany: (id: string) => void }) {
  const { can } = useSession();
  const manage = can('outreach.manage');
  const { busy, run } = useSave();
  const [editing, setEditing] = useState(false);
  const setState = (state: DemoState) =>
    void run(async () => {
      await action('outreach.demo_state', { id: d.id, state });
      await onChanged();
      onClose();
    }, t('odm.saved', { state: t(`odm.st_${state}` as MessageKey) }));
  if (editing) return <DemoForm company={d.company} contact={d.contact} demo={d} onClose={() => setEditing(false)} onSaved={async () => (await onChanged(), onClose())} />;
  const open = d.state === 'requested' || d.state === 'confirmed';
  return (
    <Dialog
      title={t('odm.title', { company: d.company_name })}
      onClose={onClose}
      footer={
        <>
          <span className="foot-actions">
            <Button variant="ghost" icon="store" onClick={() => onOpenCompany(d.company)}>
              {t('orp.open_company')}
            </Button>
          </span>
          {manage && open ? (
            <Button variant="secondary" icon="edit" onClick={() => setEditing(true)}>
              {t('odm.change')}
            </Button>
          ) : null}
        </>
      }
    >
      <div className="form-stack">
        <div className="orp-meta">
          <span className="vat-party">
            <span className="strong">{dateTime(d.at)}</span>
            <span className="muted small">{tn('odm.minutes', d.minutes)}</span>
          </span>
          <DemoBadge state={d.state} />
        </div>
        <dl className="out-facts">
          {d.contact_name ? (
            <div>
              <dt>
                <Icon name="contact" size={14} /> {t('odm.person')}
              </dt>
              <dd>
                {d.contact_name}
                {d.contact_email ? <span className="muted small"> · {d.contact_email}</span> : null}
              </dd>
            </div>
          ) : null}
          {d.phone ? (
            <div>
              <dt>
                <Icon name="phone" size={14} /> {t('out.phone')}
              </dt>
              <dd>
                <a href={`tel:${d.phone}`}>{d.phone}</a>
              </dd>
            </div>
          ) : null}
          <div>
            <dt>
              <Icon name="users" size={14} /> {t('odm.host')}
            </dt>
            <dd>{d.owner_name ?? '—'}</dd>
          </div>
          <div>
            <dt>
              <Icon name={d.source === 'link' ? 'link' : 'edit'} size={14} /> {t('odm.how')}
            </dt>
            <dd>{d.source === 'link' ? t('odm.from_link') : t('odm.by_hand')}</dd>
          </div>
        </dl>
        {d.note ? <p className="out-note">{d.note}</p> : null}
        {manage ? (
          <div className="row-actions odm-actions">
            {d.state === 'requested' ? (
              <Button variant="primary" icon="checkCircle" busy={busy} onClick={() => setState('confirmed')}>
                {t('odm.confirm')}
              </Button>
            ) : null}
            {open ? (
              <>
                <Button variant="secondary" icon="check" busy={busy} onClick={() => setState('done')}>
                  {t('odm.given')}
                </Button>
                <Button variant="ghost" icon="alert" busy={busy} onClick={() => setState('no_show')}>
                  {t('odm.no_show')}
                </Button>
                <Button variant="ghost" icon="x" busy={busy} onClick={() => setState('cancelled')}>
                  {t('odm.cancel')}
                </Button>
              </>
            ) : (
              <Button variant="ghost" icon="undo" busy={busy} onClick={() => setState('confirmed')}>
                {t('odm.reopen')}
              </Button>
            )}
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}

const pad = (n: number) => String(n).padStart(2, '0');
const localParts = (at: Date) => ({ day: `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`, time: `${pad(at.getHours())}:${pad(at.getMinutes())}` });

/** Arranging a demo by hand (after a call, or from a reply), or changing one. */
export function DemoForm({
  company,
  contact,
  demo,
  people,
  onClose,
  onSaved,
}: {
  company: string;
  contact: string | null;
  demo: DemoRow | null;
  people?: { id: string; name: string }[];
  onClose: () => void;
  onSaved: () => Promise<unknown>;
}) {
  const start = demo ? new Date(demo.at) : (() => {
    const d = new Date(Date.now() + 86_400_000);
    d.setHours(10, 0, 0, 0);
    return d;
  })();
  const [day, setDay] = useState(localParts(start).day);
  const [time, setTime] = useState(localParts(start).time);
  const [minutes, setMinutes] = useState(demo?.minutes ?? 30);
  const [who, setWho] = useState(contact ?? people?.[0]?.id ?? '');
  const [note, setNote] = useState(demo?.note ?? '');
  const { busy, run } = useSave();
  const at = day && time ? new Date(`${day}T${time}`) : null;
  return (
    <Dialog
      title={demo ? t('odm.change') : t('odm.arrange')}
      onClose={onClose}
      footer={
        <Button
          variant="primary"
          icon="calendar"
          busy={busy}
          disabled={!at || Number.isNaN(at.getTime())}
          onClick={() =>
            void run(async () => {
              await action('outreach.save_demo', { id: demo?.id ?? null, company, contact: who || null, at: at!.toISOString(), minutes, owner: null, note: note.trim() || null });
              await onSaved();
            }, demo ? t('odm.changed') : t('odm.arranged'))
          }
        >
          {demo ? t('odm.save') : t('odm.arrange_save')}
        </Button>
      }
    >
      <div className="form-stack">
        {!demo ? <p className="muted">{t('odm.arrange_hint')}</p> : null}
        {people && people.length ? (
          <Field label={t('odm.person')}>
            <select className="input" value={who} onChange={(e) => setWho(e.target.value)}>
              {people.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
        <div className="form-grid">
          <Field label={t('odm.day')} required>
            <input className="input" type="date" value={day} onChange={(e) => setDay(e.target.value)} />
          </Field>
          <Field label={t('odm.time')} required>
            <input className="input" type="time" step={900} value={time} onChange={(e) => setTime(e.target.value)} />
          </Field>
        </div>
        <Field label={t('odm.length')}>
          <select className="input" value={minutes} onChange={(e) => setMinutes(Number(e.target.value))}>
            {[15, 20, 30, 45, 60, 90].map((m) => (
              <option key={m} value={m}>
                {tn('odm.minutes', m)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('odm.note')} counter={`${note.length}/1000`}>
          <textarea className="input" rows={3} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

// ---- results ------------------------------------------------------------------------------------

type Period = 30 | 90 | 365 | 0;
interface Results {
  days: Period;
  funnel: { people: number; companies: number; opened: number; replied: number; interested: number; demo: number; customer: number; emails: number; bounced: number; unsubscribed: number };
  intents: { intent: Intent; n: number }[];
  templates: { id: string; name: string; sent: number; opened: number; replied: number }[];
  sequences: { id: string; name: string; people: number; answered: number; demos: number }[];
  owners: { id: string; name: string; people: number; replied: number; demos: number }[];
}
const pct = (n: number, of: number) => (of ? `${Math.round((n / of) * 100)}%` : '—');

export function ResultsTab() {
  const [days, setDays] = useState<Period>(90);
  const { data } = useAction<Results>('outreach.results', { days });
  const f = data?.funnel;
  const steps = f
    ? [
        { key: 'people', icon: 'send' as IconName, n: f.people, of: f.people, hint: tn('ors.at_companies', f.companies) },
        { key: 'opened', icon: 'eye' as IconName, n: f.opened, of: f.people, hint: null },
        { key: 'replied', icon: 'reply' as IconName, n: f.replied, of: f.companies, hint: null },
        { key: 'interested', icon: 'trendUp' as IconName, n: f.interested, of: f.companies, hint: null },
        { key: 'demo', icon: 'calendar' as IconName, n: f.demo, of: f.companies, hint: null },
        { key: 'customer', icon: 'checkCircle' as IconName, n: f.customer, of: f.companies, hint: null },
      ]
    : [];
  const top = Math.max(1, ...steps.map((s) => s.n));
  const replies = (data?.intents ?? []).reduce((a, b) => a + b.n, 0);
  return (
    <section className="out-section ors-page" aria-label={t('out.tab_results')}>
      <div className="out-filters">
        <div className="segmented out-seg" role="group" aria-label={t('ors.period')}>
          {([30, 90, 365, 0] as Period[]).map((p) => (
            <button key={p} type="button" aria-pressed={days === p} className={days === p ? 'active' : ''} onClick={() => setDays(p)}>
              {p ? tn('ors.days', p) : t('ors.all_time')}
            </button>
          ))}
        </div>
      </div>
      {!data || !f ? (
        <Skeleton h={320} />
      ) : !f.people ? (
        <EmptyState icon="chart" title={t('ors.empty')} body={t('ors.empty_hint')} />
      ) : (
        <>
          <section className="rep-kpis" aria-label={t('ors.glance')}>
            <Kpi label={t('ors.k_emails')} value={String(f.emails)} hint={tn('ors.k_people', f.people)} />
            <Kpi label={t('ors.k_reply_rate')} value={pct(f.replied, f.companies)} hint={tn('ors.k_replied', f.replied)} />
            <Kpi label={t('ors.k_demo_rate')} value={pct(f.demo, f.companies)} hint={tn('ors.k_demos', f.demo)} />
            <Kpi label={t('ors.k_bounced')} value={pct(f.bounced, f.emails + f.bounced)} hint={tn('ors.k_unsubscribed', f.unsubscribed)} warn={f.emails + f.bounced > 20 && f.bounced / (f.emails + f.bounced) > 0.05} />
          </section>
          <section className="panel" aria-labelledby="ors-funnel">
            <div className="panel-head">
              <h2 id="ors-funnel">{t('ors.funnel')}</h2>
              <span className="muted small">{t('ors.funnel_hint')}</span>
            </div>
            <ol className="ors-funnel">
              {steps.map((s, i) => (
                <li key={s.key}>
                  <span className="ors-label">
                    <Icon name={s.icon} size={16} />
                    {t(`ors.f_${s.key}` as MessageKey)}
                  </span>
                  <span className="ors-bar" aria-hidden="true">
                    <span style={{ width: `${Math.max(2, (s.n / top) * 100)}%` }} />
                  </span>
                  <span className="ors-num">
                    <span className="strong">{s.n}</span>
                    <span className="muted small">{i === 0 ? s.hint : pct(s.n, s.of)}</span>
                  </span>
                </li>
              ))}
            </ol>
          </section>
          <div className="ors-grid">
            <section className="panel" aria-labelledby="ors-intents">
              <div className="panel-head">
                <h2 id="ors-intents">{t('ors.intents')}</h2>
                <span className="muted small">{tn('ors.replies_n', replies)}</span>
              </div>
              {!data.intents.length ? (
                <p className="muted">{t('ors.no_replies')}</p>
              ) : (
                <ul className="ors-intents">
                  {data.intents.map((i) => (
                    <li key={i.intent}>
                      <IntentBadge intent={i.intent} />
                      <span className="ors-bar" aria-hidden="true">
                        <span style={{ width: `${(i.n / replies) * 100}%` }} />
                      </span>
                      <span className="strong">{i.n}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
            <section className="panel" aria-labelledby="ors-owners">
              <div className="panel-head">
                <h2 id="ors-owners">{t('ors.owners')}</h2>
              </div>
              {!data.owners.length ? (
                <p className="muted">{t('ors.no_owners')}</p>
              ) : (
                <TableWrap label={t('ors.owners')}>
                  <Table
                    view="outreach.results.owners"
                    head={[t('ors.c_owner'), t('ors.c_people'), t('ors.c_replied'), t('ors.c_demos')]}
                    left={[0]}
                    rows={data.owners.map((o) => ({ key: o.id, sort: [o.name.toLowerCase(), o.people, o.replied, o.demos], cells: [o.name, String(o.people), String(o.replied), String(o.demos)] }))}
                  />
                </TableWrap>
              )}
            </section>
          </div>
          <section className="panel" aria-labelledby="ors-templates">
            <div className="panel-head">
              <h2 id="ors-templates">{t('ors.templates')}</h2>
              <span className="muted small">{t('ors.templates_hint')}</span>
            </div>
            {!data.templates.length ? (
              <p className="muted">{t('ors.no_templates')}</p>
            ) : (
              <TableWrap label={t('ors.templates')}>
                <Table
                  view="outreach.results.templates"
                  head={[t('ors.c_template'), t('ors.c_sent'), t('ors.c_opened'), t('ors.c_replied')]}
                  left={[0]}
                  rows={data.templates.map((x) => ({
                    key: x.id,
                    sort: [x.name.toLowerCase(), x.sent, x.opened / Math.max(1, x.sent), x.replied / Math.max(1, x.sent)],
                    cells: [x.name, String(x.sent), pct(x.opened, x.sent), pct(x.replied, x.sent)],
                  }))}
                />
              </TableWrap>
            )}
          </section>
          <section className="panel" aria-labelledby="ors-sequences">
            <div className="panel-head">
              <h2 id="ors-sequences">{t('ors.sequences')}</h2>
            </div>
            {!data.sequences.length ? (
              <p className="muted">{t('ors.no_sequences')}</p>
            ) : (
              <TableWrap label={t('ors.sequences')}>
                <Table
                  view="outreach.results.sequences"
                  head={[t('ors.c_sequence'), t('ors.c_people'), t('ors.c_answered'), t('ors.c_demos')]}
                  left={[0]}
                  rows={data.sequences.map((x) => ({
                    key: x.id,
                    sort: [x.name.toLowerCase(), x.people, x.answered, x.demos],
                    cells: [x.name, String(x.people), `${x.answered} · ${pct(x.answered, x.people)}`, String(x.demos)],
                  }))}
                />
              </TableWrap>
            )}
          </section>
        </>
      )}
    </section>
  );
}
